import { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { ChevronDown, Plus, Trash2, Loader2, MessageSquare, Pencil } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { ChatSessionInstance } from "@/stores/chat/ChatStore";
import { useChatStore } from "@/hooks/useChatStore";
import { useLanguage } from "@/lib/i18n/LanguageContext";

/**
 * Format a date string to relative time (e.g., "2h ago", "Yesterday", "Feb 4")
 */
function formatRelativeTime(dateString: string | null): string {
  if (!dateString) return "";

  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays}d ago`;

  // For older dates, show month and day
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export interface SessionSelectorProps {
  /** List of available sessions */
  sessions: ChatSessionInstance[];
  /** Currently active session ID */
  activeSessionId: string | undefined;
  /** Callback when a session is selected */
  onSessionSelect: (sessionId: string) => void;
  /** Callback to create a new session */
  onSessionCreate: () => void;
  /** Callback to archive a session */
  onSessionArchive: (sessionId: string) => void;
  /** Callback to rename a session */
  onSessionRename: (sessionId: string, title: string) => Promise<void> | void;
  /** Whether sessions are currently loading */
  isLoading: boolean;
  /** Whether the dropdown includes its own New Chat action. */
  showCreateAction?: boolean;
  /** Render the header's compact current-chat trigger instead of the bordered wide trigger. */
  iconTrigger?: boolean;
  /** Render the compact trigger with contrast suitable for a dark header. */
  inverseTrigger?: boolean;
}

/**
 * SessionSelector - Dropdown for managing multiple chat sessions.
 *
 * Features:
 * - Displays current session title as trigger
 * - Lists all active sessions with relative timestamps
 * - Archive button (trash icon) on hover for each session
 * - "New Chat" button at bottom
 * - Loading spinner when sessions are loading
 */
export const SessionSelector = observer(function SessionSelector({
  sessions,
  activeSessionId,
  onSessionSelect,
  onSessionCreate,
  onSessionArchive,
  onSessionRename,
  isLoading,
  showCreateAction = true,
  iconTrigger = false,
  inverseTrigger = false,
}: SessionSelectorProps) {
  const { t } = useLanguage();
  const chatStore = useChatStore();
  const activeSession = sessions.find((s) => s.id === activeSessionId);

  // Dynamic dropdown width based on widget size (min 280, max 500)
  const dropdownWidth = Math.min(500, Math.max(280, chatStore.widgetSize.width - 32));
  const [editingSessionId, setEditingSessionId] = useState<string | null>(null);
  const [draftTitle, setDraftTitle] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [archiveConfirmState, setArchiveConfirmState] = useState<{
    open: boolean;
    sessionId: string | null;
    title: string | null;
  }>({ open: false, sessionId: null, title: null });

  // Truncate title based on available width
  const titleMaxChars = dropdownWidth >= 400 ? 50 : dropdownWidth >= 320 ? 35 : 25;
  const activeTitle = activeSession?.title.trim() || t.documents.chatSidebar.newChat;
  const displayTitle =
    activeTitle.length > titleMaxChars ? `${activeTitle.slice(0, titleMaxChars)}...` : activeTitle;

  useEffect(() => {
    if (editingSessionId && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingSessionId]);

  const startEdit = (sessionId: string, title: string) => {
    setEditingSessionId(sessionId);
    setDraftTitle(title);
  };

  const cancelEdit = () => {
    if (isSaving) return;
    setEditingSessionId(null);
    setDraftTitle("");
  };

  const saveEdit = async (sessionId: string, currentTitle: string) => {
    if (isSaving) return;
    const normalized = draftTitle.trim();
    if (!normalized) {
      cancelEdit();
      return;
    }
    const safeTitle = normalized.slice(0, 255);
    if (safeTitle === currentTitle) {
      cancelEdit();
      return;
    }
    try {
      setIsSaving(true);
      await onSessionRename(sessionId, safeTitle);
      setEditingSessionId(null);
      setDraftTitle("");
    } catch (error) {
      console.error("Failed to rename chat session:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const executeArchive = () => {
    if (archiveConfirmState.sessionId) {
      onSessionArchive(archiveConfirmState.sessionId);
    }
    setArchiveConfirmState({ open: false, sessionId: null, title: null });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            iconTrigger
              ? cn(
                  "h-8 max-w-[220px] gap-1.5 rounded-md px-2",
                  inverseTrigger
                    ? "text-neutral-100 hover:bg-white/10 hover:text-white focus-visible:ring-white focus-visible:ring-offset-[#262626] data-[state=open]:bg-white/10 data-[state=open]:text-white"
                    : "text-neutral-700 hover:bg-neutral-100 hover:text-neutral-900"
                )
              : [
                  "h-7 w-full justify-between gap-1 px-2",
                  "rounded border border-neutral-200 bg-neutral-100 hover:bg-neutral-200",
                  "text-xs text-neutral-600 hover:text-neutral-900",
                ]
          )}
          disabled={isLoading && sessions.length === 0}
          aria-label={iconTrigger ? t.documents.chatSidebar.chatHistory : undefined}
          title={iconTrigger ? t.documents.chatSidebar.chatHistory : undefined}
        >
          {iconTrigger ? (
            <>
              <span className="min-w-0 truncate text-sm font-medium">{displayTitle}</span>
              {isLoading ? (
                <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
              ) : (
                <ChevronDown className="h-3.5 w-3.5 shrink-0" />
              )}
            </>
          ) : (
            <>
              <span className="flex items-center gap-1.5 truncate">
                <MessageSquare className="h-3 w-3 shrink-0" />
                <span className="truncate">{displayTitle}</span>
              </span>
              {isLoading ? (
                <Loader2 className="h-3 w-3 shrink-0 animate-spin" />
              ) : (
                <ChevronDown className="h-3 w-3 shrink-0" />
              )}
            </>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="start"
        className="border-neutral-200 bg-white"
        style={{ width: `${dropdownWidth}px` }}
        sideOffset={4}
      >
        {/* Session list */}
        {sessions.length > 0 ? (
          <div className="session-list-scroll max-h-[240px] overflow-y-auto">
            {sessions.map((session) => {
              const isEditing = editingSessionId === session.id;
              const sessionTitle = session.title.trim() || t.documents.chatSidebar.newChat;

              if (isEditing) {
                return (
                  <div
                    key={session.id}
                    className={cn("flex items-center gap-2 px-2 py-2", "bg-neutral-100")}
                  >
                    <input
                      ref={inputRef}
                      value={draftTitle}
                      onChange={(e) => setDraftTitle(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          saveEdit(session.id, session.title);
                        }
                        if (e.key === "Escape") {
                          e.preventDefault();
                          cancelEdit();
                        }
                      }}
                      onClick={(e) => e.stopPropagation()}
                      onBlur={() => saveEdit(session.id, session.title)}
                      className={cn(
                        "h-7 w-full rounded border border-neutral-200",
                        "bg-white px-2 text-sm text-neutral-900",
                        "focus:border-primary focus:outline-none"
                      )}
                      maxLength={255}
                    />
                  </div>
                );
              }

              return (
                <DropdownMenuItem
                  key={session.id}
                  className={cn(
                    "group flex cursor-pointer items-center justify-between gap-2 px-2 py-2",
                    "hover:bg-neutral-100 focus:bg-neutral-100",
                    session.id === activeSessionId && "bg-neutral-100"
                  )}
                  onSelect={(e) => {
                    e.preventDefault();
                    onSessionSelect(session.id);
                  }}
                >
                  <div className="flex min-w-0 flex-1 flex-col">
                    <span
                      title={sessionTitle}
                      className={cn(
                        "truncate text-sm",
                        session.id === activeSessionId
                          ? "font-medium text-primary"
                          : "text-neutral-800"
                      )}
                    >
                      {sessionTitle}
                    </span>
                    <span className="text-xs text-neutral-500">
                      {formatRelativeTime(session.lastMessageAt || session.createdAt)}
                      {session.messageCount > 0 &&
                        ` · ${session.messageCount} ${t.documents.chatSidebar.messages}`}
                    </span>
                  </div>

                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className={cn(
                        "h-6 w-6 shrink-0",
                        "opacity-0 transition-opacity group-hover:opacity-100",
                        "text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"
                      )}
                      onPointerDown={(e) => e.preventDefault()}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        startEdit(session.id, session.title);
                      }}
                      aria-label={t.documents.chatSidebar.editChatName}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>

                    {/* Archive button - only show on hover if not the only session */}
                    {sessions.length > 1 && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className={cn(
                          "h-6 w-6 shrink-0",
                          "opacity-0 transition-opacity group-hover:opacity-100",
                          "text-neutral-500 hover:bg-red-400/10 hover:text-red-400"
                        )}
                        onClick={(e) => {
                          e.stopPropagation();
                          setArchiveConfirmState({
                            open: true,
                            sessionId: session.id,
                            title: sessionTitle,
                          });
                        }}
                        aria-label={t.documents.chatSidebar.archiveChat}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </DropdownMenuItem>
              );
            })}
          </div>
        ) : (
          <div className="px-2 py-3 text-center text-sm text-neutral-500">
            {isLoading ? t.documents.chatSidebar.loadingChats : t.documents.chatSidebar.noChatsYet}
          </div>
        )}

        {showCreateAction && (
          <>
            <DropdownMenuSeparator className="bg-neutral-200" />

            {/* New Chat button */}
            <DropdownMenuItem
              className={cn(
                "flex cursor-pointer items-center gap-2 px-2 py-2",
                "text-primary hover:text-primary focus:text-primary",
                "hover:bg-neutral-100 focus:bg-neutral-100"
              )}
              onSelect={(e) => {
                e.preventDefault();
                onSessionCreate();
              }}
            >
              <Plus className="h-4 w-4" />
              <span className="text-sm font-medium">{t.documents.chatSidebar.newChat}</span>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>

      <ConfirmDialog
        open={archiveConfirmState.open}
        onOpenChange={(open) => {
          if (!open) {
            setArchiveConfirmState({ open: false, sessionId: null, title: null });
          }
        }}
        title={t.documents.chatSidebar.deleteChatTitle}
        description={t.documents.chatSidebar.deleteChatDescription}
        onConfirm={executeArchive}
        confirmText={t.documents.chatSidebar.deleteChatConfirm}
        confirmVariant="destructive"
      />
    </DropdownMenu>
  );
});

SessionSelector.displayName = "SessionSelector";
